import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// ---------------------------------------------------------------------------
// Module-level request helpers. Keeping the async logic (and its local
// variables) outside the component means the memoized callbacks below are
// thin bindings whose bodies reference only stable, dependency-listed values.
// ---------------------------------------------------------------------------
async function loadSessionInto(setUser, setBooting) {
  try {
    const { data } = await api.get("/auth/me");
    setUser(data);
  } catch {
    // No valid session or it could not be restored. This runs on every app
    // boot (including for anonymous visitors), so no error is surfaced —
    // the user is simply treated as signed out and ProtectedRoute redirects
    // to the login page.
    setUser(null);
  } finally {
    setBooting(false);
  }
}

async function performLogin(email, password, setUser) {
  try {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: formatApiError(e.response?.data?.detail) };
  }
}

async function performLogout(setUser) {
  try {
    await api.post("/auth/logout");
  } catch {
    // Server-side sign-out could not be confirmed (e.g. network issue).
    // The local session is still cleared below and the auth cookies are
    // short-lived, so inform the user without technical detail.
    toast.error("You have been signed out on this device, but the server could not be reached.");
  }
  setUser(null);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=logged out
  const [booting, setBooting] = useState(true);

  // The module helpers are intentionally non-reactive; a ref keeps them
  // referentially stable in component scope so every dependency array below
  // is complete without ever triggering reruns.
  const helpers = useRef({ loadSessionInto, performLogin, performLogout });

  const loadMe = useCallback(
    () => helpers.current.loadSessionInto(setUser, setBooting),
    [helpers, setUser, setBooting]
  );

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(
    (email, password) => helpers.current.performLogin(email, password, setUser),
    [helpers, setUser]
  );

  const logout = useCallback(() => helpers.current.performLogout(setUser), [helpers, setUser]);

  const isAdmin = user?.role === "administrator" || user?.role === "global_administrator";
  const isGlobalAdmin = user?.role === "global_administrator";

  // 30-minute inactivity timeout: only genuine user events (click/key/scroll)
  // count. A throttled signal informs the server; warning at 28 min; local
  // sign-out at 30. Background polling never touches the timer.
  useEffect(() => {
    if (!user) return undefined;
    let lastActivity = Date.now();
    let lastPing = 0;
    let warned = false;
    const onActivity = () => {
      lastActivity = Date.now();
      warned = false;
      window.dispatchEvent(new CustomEvent("tfs-hide-idle-warning"));
      if (Date.now() - lastPing > 120000) {
        lastPing = Date.now();
        api.post("/presence/activity").catch(() => {});
      }
    };
    const events = ["mousedown", "keydown", "wheel", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity));
    const timer = setInterval(() => {
      const idleMs = Date.now() - lastActivity; // wall clock — sleep can't pause it
      if (idleMs >= 30 * 60 * 1000) {
        sessionStorage.setItem("idle_logout", "1");
        helpers.current.performLogout(setUser);
        window.location.href = "/login";
      } else if (idleMs >= 28 * 60 * 1000 && !warned) {
        warned = true;
        window.dispatchEvent(new CustomEvent("tfs-idle-warning"));
      }
    }, 5000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [user]);


  // Stable context value: avoids re-rendering every consumer on unrelated
  // AuthProvider renders (all callbacks above are memoized).
  const value = useMemo(
    () => ({ user, booting, login, logout, isAdmin, isGlobalAdmin, refresh: loadMe }),
    [user, booting, login, logout, isAdmin, isGlobalAdmin, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
